import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {
    MatAutocompleteModule,
    MatAutocompleteSelectedEvent
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PlantsService } from '../services/plants.service';
import {
    Observable,
    catchError,
    distinctUntilChanged,
    map,
    of,
    startWith
} from 'rxjs';
import {
    requirePlantIdValidator,
    atLeastOneMeteringPoint,
    exactlyOneMain
} from '../shared/validators';
import { MatCardModule } from '@angular/material/card';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

type MeteringPoint = {
    meteringPointId: string;
    meterType: string;
    isMain: boolean;
};

type MpRowForm = {
    meteringPointId: FormControl<string>;
    meterType: FormControl<string>;
    isMain: FormControl<boolean>;
    meterTypeSearch: FormControl<string>;
};

type InverterFormModel = {
    id: FormControl<any>;
    brand: FormControl<string>;
    batteryChargePowerRateKw: FormControl<string>;
    serialNumber: FormControl<string>;
    maxProductionCapacity: FormControl<string>;
    meteringPointIds: FormControl<MeteringPoint[]>;
    model: FormControl<string>;
    plantId: FormControl<number>;
    solarFactorThreshold: FormControl<string>;
    totalBatteryCapacityKwhsolarPanels: FormControl<string>;
    totalPanelPowerKwh: FormControl<string>;
    meteringPoints: FormArray<FormGroup<MpRowForm>>;
};

@Component({
    selector: 'app-add-inverter',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatAutocompleteModule,
        MatCheckboxModule,
        MatIconModule,
        MatCardModule,
        TranslatePipe
    ],
    templateUrl: './add-inverter.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        './add-inverter.component.scss',
        '../add-plant/add-plant.component.scss'
    ]
})
export class AddInverterComponent implements OnInit {
    private readonly solarThresholdMin = 0;
    private readonly solarThresholdMax = 1;

    invertersForm: FormGroup<InverterFormModel> = this.fb.group<
        InverterFormModel
    >({
        id: this.fb.control<any>(undefined),
        brand: this.fb.control('', {
            nonNullable: true,
            validators: [Validators.required]
        }),
        batteryChargePowerRateKw: this.fb.control('', {
            nonNullable: true,
            validators: [Validators.required]
        }),
        serialNumber: this.fb.control('', {
            nonNullable: true,
            validators: [Validators.required]
        }),
        maxProductionCapacity: this.fb.control('', { nonNullable: true }),
        meteringPointIds: this.fb.control<MeteringPoint[]>([], {
            nonNullable: true
        }),
        model: this.fb.control('', {
            nonNullable: true,
            validators: [Validators.required]
        }),
        plantId: this.fb.control(0, { nonNullable: true }),
        solarFactorThreshold: this.fb.control('', {
            nonNullable: true,
            validators: [Validators.required, this.solarThresholdValidator()]
        }),
        totalBatteryCapacityKwhsolarPanels: this.fb.control('', {
            nonNullable: true,
            validators: [Validators.required]
        }),
        totalPanelPowerKwh: this.fb.control('', {
            nonNullable: true,
            validators: [Validators.required]
        }),
        meteringPoints: this.fb.array<FormGroup<MpRowForm>>([])
    });

    private brandToModels = new Map<string, string[]>();
    private brandChangeHooked = false;

    brandOptions: string[] = [];
    modelOptions: string[] = [];
    meterTypeOptions: { title: string; description?: string }[] = [];

    brandSearchCtrl = new FormControl<string>('', { nonNullable: true });
    modelSearchCtrl = new FormControl<string>('', { nonNullable: true });

    filteredBrands$: Observable<string[]> = of([]);
    filteredModels$: Observable<string[]> = of([]);

    isSubmittingInverter = false;
    plantId?: number;

    constructor(
        private fb: FormBuilder,
        private plantsSvc: PlantsService,
        private sb: MatSnackBar,
        private route: ActivatedRoute,
        private router: Router,
        private translate: TranslateService
    ) {}

    get meteringPointsFA(): FormArray<FormGroup<MpRowForm>> {
        return this.invertersForm.controls.meteringPoints;
    }

    ngOnInit(): void {
        const plantIdStr = this.route.snapshot.paramMap.get('plantId');
        const plantId = plantIdStr ? Number(plantIdStr) : null;

        if (!plantId || Number.isNaN(plantId)) {
            console.error(this.t('invalid_plant_id_in_route'));
            this.router.navigate(['/plants']);
            return;
        }

        this.plantId = plantId;
        this.invertersForm.controls.plantId.setValue(this.plantId);

        if (this.meteringPointsFA.length === 0) {
            this.addMeteringPoint();
        }

        this.invertersForm.setValidators([
            requirePlantIdValidator(),
            atLeastOneMeteringPoint(),
            exactlyOneMain()
        ]);
        this.invertersForm.updateValueAndValidity();

        this.hookBrandChange();
        this.loadInverterPrerequisites();

        this.brandSearchCtrl.valueChanges.subscribe(v => {
            this.invertersForm.controls.brand.setValue(v ?? '', {
                emitEvent: true
            });
            this.invertersForm.controls.brand.updateValueAndValidity({
                onlySelf: true
            });
        });

        this.modelSearchCtrl.valueChanges.subscribe(v => {
            this.invertersForm.controls.model.setValue(v ?? '', {
                emitEvent: true
            });
            this.invertersForm.controls.model.updateValueAndValidity({
                onlySelf: true
            });
        });
    }

    private t(key: string, params?: Record<string, unknown>): string {
        return this.translate.instant(key, params);
    }

    private createFilter$(
        ctrl: FormControl<string>,
        getOptions: () => string[]
    ): Observable<string[]> {
        return ctrl.valueChanges.pipe(
            startWith(ctrl.value ?? ''),
            map(v => (v ?? '').toLowerCase()),
            map(q => getOptions().filter(x => x.toLowerCase().includes(q)))
        );
    }

    private solarThresholdValidator(
        min = this.solarThresholdMin,
        max = this.solarThresholdMax
    ): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const raw = String(control.value ?? '').trim();

            if (!raw) {
                return null;
            }

            const normalized = raw.replace(',', '.');
            const parsed = Number(normalized);

            if (Number.isNaN(parsed)) {
                return { solarThresholdInvalid: true };
            }

            if (parsed < min || parsed > max) {
                return {
                    solarThresholdOutOfRange: {
                        min,
                        max,
                        actual: parsed
                    }
                };
            }

            return null;
        };
    }

    private normalizeSolarFactorThreshold(value: any): string {
        return String(value ?? '')
            .trim()
            .replace(',', '.');
    }

    private getFriendlyErrorMessage(err: any): string {
        const raw =
            String(err?.statusMessage ?? '') ||
            String(err?.message ?? '') ||
            String(err?.error?.message ?? '') ||
            this.t('something_went_wrong');

        const message = raw.toLowerCase();

        if (
            message.includes('solarfactorthreshold') &&
            message.includes('out of range')
        ) {
            return this.t('solar_threshold_out_of_range');
        }

        if (
            message.includes('solarfactorthreshold') &&
            message.includes('not valid')
        ) {
            return this.t('solar_threshold_invalid_numeric_value');
        }

        if (
            message.includes('exactly one metering point') ||
            message.includes('maincountinvalid')
        ) {
            return this.t('exactly_one_metering_point_main');
        }

        if (
            message.includes('invalid plantid') ||
            message.includes('plantid not set')
        ) {
            return this.t('plant_id_missing_or_invalid');
        }

        if (message.includes('invalidargument')) {
            return this.t('some_inverter_fields_invalid');
        }

        return raw;
    }

    private handleError(err: any): void {
        console.error('[add-inverter]', err);
        this.sb.open(this.getFriendlyErrorMessage(err), this.t('close'), {
            panelClass: ['snack-error'],
            horizontalPosition: 'right',
            verticalPosition: 'top',
            duration: 7000
        });
    }

    private validateSolarThresholdBeforeSubmit(): boolean {
        const ctrl = this.invertersForm.controls.solarFactorThreshold;
        ctrl.updateValueAndValidity({ onlySelf: true });

        if (ctrl.hasError('required')) {
            ctrl.markAsTouched();
            this.sb.open(this.t('solar_threshold_required'), this.t('close'), {
                panelClass: ['snack-error']
            });
            return false;
        }

        if (ctrl.hasError('solarThresholdInvalid')) {
            ctrl.markAsTouched();
            this.sb.open(this.t('solar_threshold_invalid'), this.t('close'), {
                panelClass: ['snack-error']
            });
            return false;
        }

        if (ctrl.hasError('solarThresholdOutOfRange')) {
            ctrl.markAsTouched();
            this.sb.open(
                this.t('solar_threshold_out_of_range'),
                this.t('close'),
                {
                    panelClass: ['snack-error']
                }
            );
            return false;
        }

        return true;
    }

    private setPrerequisiteOptionsAndInit(res: any) {
        this.brandToModels.clear();

        const brands = Array.isArray(res?.brands) ? res.brands : [];
        this.brandOptions = brands
            .map((b: any) => b?.title)
            .filter((t: any) => !!t);

        brands.forEach((b: any) => {
            const title = b?.title ?? '';
            const modelsArr = Array.isArray(b?.models) ? b.models : [];
            const modelTitles = modelsArr
                .map((m: any) => m?.title)
                .filter((t: any) => !!t);
            this.brandToModels.set(title, modelTitles);
        });

        this.meterTypeOptions = (res?.meterTypes ?? []).map((t: any) => ({
            title: t.title,
            description: t.description
        }));

        const currentBrand = (
            this.invertersForm.controls.brand.value ?? ''
        ).trim();
        this.modelOptions = this.brandToModels.get(currentBrand) ?? [];

        this.initAutocompleteFilters();
    }

    private initAutocompleteFilters(): void {
        this.brandSearchCtrl.setValue(
            this.invertersForm.controls.brand.value ?? '',
            { emitEvent: false }
        );
        this.modelSearchCtrl.setValue(
            this.invertersForm.controls.model.value ?? '',
            { emitEvent: false }
        );

        this.filteredBrands$ = this.createFilter$(
            this.brandSearchCtrl,
            () => this.brandOptions
        );
        this.filteredModels$ = this.createFilter$(
            this.modelSearchCtrl,
            () => this.modelOptions
        );

        const currentBrand = (
            this.invertersForm.controls.brand.value ?? ''
        ).trim();
        this.onBrandChanged(currentBrand, false);
    }

    private loadInverterPrerequisites() {
        this.plantsSvc
            .getAddInverterPrerequisites()
            .pipe(
                catchError((err: any) => {
                    this.handleError(err);
                    return of(null);
                })
            )
            .subscribe(res => {
                if (!res) return;
                this.setPrerequisiteOptionsAndInit(res);
            });
    }

    private hookBrandChange() {
        if (this.brandChangeHooked) return;
        this.brandChangeHooked = true;

        const brandCtrl = this.invertersForm.controls.brand;
        brandCtrl.valueChanges.pipe(distinctUntilChanged()).subscribe(brand => {
            const b = String(brand ?? '').trim();
            this.onBrandChanged(b, true);
        });
    }

    private onBrandChanged(brand: string, clearModel = true) {
        if (clearModel) {
            this.invertersForm.controls.model.setValue('', {
                emitEvent: false
            });
            this.modelSearchCtrl.setValue('', { emitEvent: false });
        }

        this.modelOptions = this.brandToModels.get(brand) ?? [];
        this.modelSearchCtrl.updateValueAndValidity({
            onlySelf: true,
            emitEvent: true
        });
    }

    onBrandSelected(ev: MatAutocompleteSelectedEvent) {
        const val = (ev.option.value as string) ?? '';
        this.invertersForm.controls.brand.setValue(val);
        this.invertersForm.controls.brand.updateValueAndValidity();
        this.onBrandChanged(val.trim());
    }

    addMeteringPoint() {
        const isFirst = this.meteringPointsFA.length === 0;

        const row = this.fb.group<MpRowForm>({
            meteringPointId: this.fb.control('', {
                nonNullable: true,
                validators: [Validators.required]
            }),
            meterType: this.fb.control('', {
                nonNullable: true,
                validators: [Validators.required]
            }),
            isMain: this.fb.control(isFirst, { nonNullable: true }),
            meterTypeSearch: this.fb.control('', { nonNullable: true })
        });

        row.controls.meterTypeSearch.valueChanges.subscribe(v => {
            row.controls.meterType.setValue(v ?? '', { emitEvent: false });
            row.controls.meterType.updateValueAndValidity({ onlySelf: true });
            this.syncMeteringPointsControl();
            this.invertersForm.updateValueAndValidity();
        });

        this.meteringPointsFA.push(row);
        this.invertersForm.updateValueAndValidity();
        this.syncMeteringPointsControl();
    }

    removeMeteringPoint(i: number) {
        this.meteringPointsFA.removeAt(i);
        this.syncMeteringPointsControl();
        this.invertersForm.updateValueAndValidity();
    }

    setAsMain(i: number) {
        this.meteringPointsFA.controls.forEach((g, idx) => {
            g.controls.isMain.setValue(idx === i);
        });
        this.syncMeteringPointsControl();
        this.invertersForm.updateValueAndValidity();
    }

    getMeterTypeFiltered(
        i: number
    ): Observable<{ title: string; description?: string }[]> {
        const row = this.meteringPointsFA.at(i);
        const ctrl = row.controls.meterTypeSearch ?? row.controls.meterType;

        return ctrl.valueChanges.pipe(
            startWith(ctrl.value ?? ''),
            map((v: any) => String(v ?? '').toLowerCase()),
            map((q: string) =>
                this.meterTypeOptions.filter(
                    mt =>
                        mt.title.toLowerCase().includes(q) ||
                        (mt.description ?? '').toLowerCase().includes(q)
                )
            )
        );
    }

    private syncMeteringPointsControl() {
        const arr: MeteringPoint[] = this.meteringPointsFA.controls.map(g => ({
            meteringPointId: g.controls.meteringPointId.value,
            meterType: g.controls.meterType.value,
            isMain: g.controls.isMain.value
        }));

        this.invertersForm.controls.meteringPointIds.setValue(arr);
    }

    private buildAddInverterRequest() {
        this.syncMeteringPointsControl();
        const f = this.invertersForm.getRawValue();

        return {
            batteryChargePowerRateKw: String(
                f.batteryChargePowerRateKw ?? ''
            ).trim(),
            brand: String(f.brand ?? '').trim(),
            maxProductionCapacity: String(f.maxProductionCapacity ?? '').trim(),
            meteringPointIds: (f.meteringPointIds ?? []).map((mp: any) => ({
                meteringPointId: String(mp.meteringPointId ?? '').trim(),
                meterType: String(mp.meterType ?? '').trim(),
                isMain: !!mp.isMain
            })),
            model: String(f.model ?? '').trim(),
            plantId: Number(this.plantId ?? f.plantId ?? 0),
            serialNumber: String(f.serialNumber ?? '').trim(),
            totalBatteryCapacityKwh: String(
                f.totalBatteryCapacityKwhsolarPanels ?? ''
            ).trim(),
            totalPanelPowerKwh: String(f.totalPanelPowerKwh ?? '').trim(),
            solarFactorThreshold: this.normalizeSolarFactorThreshold(
                f.solarFactorThreshold
            )
        };
    }

    onSave() {
        if (!this.plantId) {
            this.sb.open(
                this.t('plant_id_not_set_submit_inverter'),
                this.t('close'),
                {
                    panelClass: ['snack-error']
                }
            );
            return;
        }

        if (!this.validateSolarThresholdBeforeSubmit()) {
            return;
        }

        if (this.invertersForm.invalid) {
            this.invertersForm.markAllAsTouched();
            this.sb.open(
                this.t('please_fix_highlighted_fields'),
                this.t('close'),
                {
                    panelClass: ['snack-error']
                }
            );
            return;
        }

        const req = this.buildAddInverterRequest();
        const mains = (req.meteringPointIds || []).filter(
            (mp: any) => !!mp.isMain
        );

        if (mains.length !== 1) {
            this.invertersForm.setErrors({ mainCountInvalid: true });
            this.sb.open(
                this.t('exactly_one_metering_point_main'),
                this.t('close'),
                {
                    panelClass: ['snack-error']
                }
            );
            return;
        }
        this.isSubmittingInverter = true;
        this.plantsSvc
            .addPlantInverter(req)
            .pipe(
                catchError((err: any) => {
                    this.isSubmittingInverter = false;
                    this.handleError(err);
                    return of(null);
                })
            )
            .subscribe(res => {
                if (!res) return;

                this.isSubmittingInverter = false;
                this.sb.open(
                    this.t('inverter_saved_successfully'),
                    this.t('close'),
                    {
                        panelClass: ['snack-success']
                    }
                );

                setTimeout(() => {
                    this.router.navigate([
                        '/plants/plants-detail',
                        this.plantId
                    ]);
                }, 3000);
            });
    }

    onMeterTypeSelected(i: number, ev: MatAutocompleteSelectedEvent) {
        const val = ev.option.value as
            | { title: string; description?: string }
            | string;
        const title = typeof val === 'string' ? val : val.title;

        const row = this.meteringPointsFA.at(i);
        row.get('meterTypeSearch')?.setValue(title, { emitEvent: false });
        row.get('meterType')?.setValue(title, { emitEvent: false });
        row.get('meterType')?.updateValueAndValidity({ onlySelf: true });

        this.syncMeteringPointIds();
        this.invertersForm.updateValueAndValidity();
    }

    private syncMeteringPointIds() {
        const arr = this.meteringPointsFA.controls.map((g: any) => ({
            meteringPointId: g.get('meteringPointId')?.value ?? '',
            meterType: g.get('meterType')?.value ?? '',
            isMain: !!g.get('isMain')?.value
        }));

        this.invertersForm
            .get('meteringPointIds')
            ?.setValue(arr, { emitEvent: false });
    }

    onCancel() {
        this.router.navigate(['/plants/all-plant-list']);
    }
}
